import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const VendorContext = createContext();

export const VendorProvider = ({ children }) => {
  const [vendors, setVendors] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    limit: 10,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    limit: filterOptions.limit,
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const fetchVendors = useCallback(async () => {
    setVendors((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/ahr/vendors${queryString}`);
      if (response.success) {
        setVendors({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setVendors({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const handleLimitChange = (newLimit) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      limit: newLimit,
      currentPage: 1, // Reset to first page when limit changes
    }));
  };

  const totalPages = vendors.pagination?.last_page || 1;

  const PaginationComponent = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
    limit: filterOptions.limit,
    onLimitChange: handleLimitChange,
  });

  const Pagination = () => PaginationComponent;

  return (
    <VendorContext.Provider
      value={{
        vendors,
        filterOptions,
        handleSearch,
        refetch: fetchVendors,
        Pagination,
      }}
    >
      {children}
    </VendorContext.Provider>
  );
};

export const useVendor = () => {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error("useVendor must be used within VendorProvider");
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(`/ahr/vendors?search=${inputValue}`);
    if (response.success) {
      return response.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};
